import { useEffect, useRef, useState } from 'react';
import { Phone, PhoneDisconnect, Robot } from '@phosphor-icons/react';
import { WidgetLink } from '../../components/VoiceWidget/WidgetLink';
import {
  DEFAULT_TERMS_URL,
  DEFAULT_PRIVACY_URL,
} from '../../components/VoiceWidget/legalDefaults';
import { useContactInfo } from '../../hooks/useContactInfo';
import { CLAIM_IN_PLACE, useInlineChatHost } from '../../context/InlineChatHostContext';
import { useVoiceAssistant } from '../../context/VoiceAssistantContext';
import { useTranslation } from '../../i18n/I18nContext';
import './ReceptionistCall.css';

/**
 * Согласие на разговор виджет хранит флагом в `localStorage`, и на голосовом
 * входе это ЗАПРЕТ: без флага он не поднимает микрофон, а показывает свой
 * поповер согласия — то есть выводит на экран окно виджета посреди звонка.
 *
 * Здесь согласие даётся на самом телефоне: нотис со ссылками стоит под
 * трубкой, человек видит его ДО нажатия, и нажатие его подтверждает. Правило
 * ровно то же, по которому виджет засчитывает согласие при открытии чата
 * («увидел нотис в баннере — согласие получено», `VoiceWidgetConnected`),
 * просто нотис показывает телефон, а не чат.
 *
 * Пишем в try/catch: в приватном режиме доступ к хранилищу бросает. Тогда
 * флага не будет, виджет спросит согласие сам — и это правильный исход:
 * звонок без согласия начаться не должен ни при каких обстоятельствах.
 */
const rememberConsent = () => {
  try {
    window.localStorage?.setItem('widget_consentGiven', 'true');
  } catch {
    // Хранилище недоступно — согласие спросит виджет.
  }
};

/** мм:сс — без часов: демонстрационный звонок столько не длится. */
const formatDuration = (seconds) =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(
    seconds % 60
  ).padStart(2, '0')}`;

/**
 * ReceptionistCall — «позвоните и поговорите с ассистентом» (#1330).
 *
 * ## Зачем блок
 *
 * Переписка выше показывает каналы, где покупатель ПИШЕТ. Но половина
 * обращений к дилеру — звонки, и вечером на них не отвечает никто: салон
 * закрыт, человек кладёт трубку и звонит следующему. Здесь показана вторая
 * половина продукта: трубку берёт ассистент.
 *
 * ## Разговор НАСТОЯЩИЙ, и начинается он по кнопке
 *
 * Раньше блок проигрывал заготовленный диалог сам по себе. Это была
 * инсценировка: посетитель смотрел мультик про звонок вместо того, чтобы
 * позвонить. Теперь кнопка поднимает трубку по-настоящему — открывает
 * голосовую сессию с тем же агентом, что отвечает в виджете, и с ним можно
 * говорить, как будто набрал номер салона.
 *
 * ## Почему это выглядит телефоном
 *
 * Не «карточка в духе телефона», а сам телефон: корпус со скруглением и
 * рамкой, вырез камеры сверху, экран вызова внутри — имя, номер, состояние
 * линии, круглая трубка внизу. Раньше здесь была обычная строка с кнопкой
 * «Call the showroom», и понять, что сейчас произойдёт ЗВОНОК, а не
 * откроется ещё один чат, было неоткуда — кнопка выглядела как любая другая
 * на странице. Промежуточный вариант — тёмная карточка с трубкой — читался
 * уже лучше, но всё ещё блоком сайта; корпус снимает вопрос совсем.
 *
 * ## Куда «приземляется» разговор
 *
 * Виджет на странице ОДИН (см. `InlineChatHostContext`: два экземпляра дерутся
 * за `browserControlWS`, ровно причина #859). Пока идёт звонок, карточка
 * занимает виджет «на месте» (`CLAIM_IN_PLACE`): голос идёт через него, а сам
 * он остаётся в мокапе переписки и показывает переписку до звонка как есть
 * (#1441 — раньше виджет уносили в скрытый контейнер у телефона, и окно чата
 * пустело на весь разговор). Звонок — отдельная сессия агента: в чат и в
 * инбокс его реплики не попадают. Взялись за чат — звонок завершается.
 *
 * Файл в `pages/shared/`, поэтому его CSS не инлайнится в Shadow DOM дилера.
 *
 * @param {object} props
 */
export const ReceptionistCall = () => {
  const { t } = useTranslation();
  const contactInfo = useContactInfo();
  const { setClaimedHost } = useInlineChatHost();
  const { openChat, closeVoiceAssistant, isolatedCallActive } = useVoiceAssistant();
  const [calling, setCalling] = useState(false);
  const [seconds, setSeconds] = useState(0);

  // Пока идёт звонок — виджет занят голосом, но остаётся в окне переписки;
  // положили трубку или ушли со страницы — захват снимается.
  // Взялись за чат во время разговора — звонок завершается (#1417), а не
  // держит чат заблокированным: `hangUp` передаём как способ уступить виджет.
  const hangUpRef = useRef(null);
  useEffect(() => {
    // Виджет остаётся в окне переписки (#1441): уносить его к телефону
    // значило оставить окно пустым на весь разговор. Захват «на месте» держит
    // только смысл «занят звонком» — взялись за чат, звонок завершается.
    setClaimedHost(
      calling ? CLAIM_IN_PLACE : null,
      () => hangUpRef.current?.()
    );
    return () => setClaimedHost(null);
  }, [calling, setClaimedHost]);

  // Счётчик разговора. Секунда — не анимация, поэтому `prefers-reduced-motion`
  // его не касается: это показание, а не движение ради движения.
  useEffect(() => {
    if (!calling) {
      setSeconds(0);
      return undefined;
    }
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [calling]);

  // Звонок кончился не по нашей кнопке — трубку положил агент, соединение не
  // поднялось, виджет закрыли, — карточка обязана это заметить, иначе она
  // так и тикала бы «в разговоре». Источник — сам виджет
  // (`isolatedCallActive`, #1441): прежний `isOpen` путь `openChat` не ставит
  // вовсе, и эта проверка молча не работала.
  //
  // Ждём, пока звонок СНАЧАЛА начнётся: между нажатием и стартом есть время
  // (микрофон, токен), когда `calling` уже true, а виджет ещё не в звонке.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (!calling) {
      wasActiveRef.current = false;
      return;
    }
    if (isolatedCallActive) {
      wasActiveRef.current = true;
    } else if (wasActiveRef.current) {
      setCalling(false);
    }
  }, [calling, isolatedCallActive]);

  // Звонок так и не начался (согласие не записалось и поповер закрыли,
  // старт упал до того, как виджет вошёл в звонок) — не тикаем «в разговоре»
  // вечно: через 60 с кладём трубку сами. Запас большой, потому что сюда же
  // входит системный запрос микрофона — новичок может думать над ним долго.
  useEffect(() => {
    if (!calling || isolatedCallActive || wasActiveRef.current) return undefined;
    const id = setTimeout(() => hangUpRef.current?.(), 60000);
    return () => clearTimeout(id);
  }, [calling, isolatedCallActive]);

  const call = () => {
    // Порядок важен: сначала согласие, потом звонок. Наоборот виджет успеет
    // решить, что согласия нет, и выставит свой поповер поверх страницы.
    rememberConsent();
    setCalling(true);
    // Звонок — отдельный канал (#1441): своя сессия агента, в чат сайта и в
    // инбокс команды его реплики не попадают, переписка после звонка та же.
    Promise.resolve(
      openChat({ mode: 'voice', action: 'receptionist_call', isolated: true })
    ).then((started) => {
      // Виджет отказался начинать (прежний звонок ещё закрывается) — не
      // держим «вызов» на экране до страховки.
      if (started === false) setCalling(false);
    });
  };

  const hangUp = () => {
    setCalling(false);
    closeVoiceAssistant();
  };
  // Свежий `hangUp` для колбэка захвата — в эффекте, а не в рендере.
  useEffect(() => {
    hangUpRef.current = hangUp;
  });

  // Ушли со страницы посреди разговора — трубку кладём, а не оставляем
  // ассистента слушать на соседней странице (ревью !1301).
  const callingRef = useRef(false);
  useEffect(() => {
    callingRef.current = calling;
  }, [calling]);
  useEffect(
    () => () => {
      if (callingRef.current) hangUpRef.current?.();
    },
    []
  );

  // Подписи под трубкой нет: зелёная трубка и так читается «позвонить», а
  // текст под ней повторял заголовок блока. Строка осталась доступным именем
  // кнопки — читалке иконки недостаточно.
  const callLabel = t('showcase.chats.call', 'Call the showroom');
  const hangUpLabel = t('showcase.chats.hangUp', 'Hang up');

  return (
    <div className="receptionist">
      {/* Корпус. Вырез камеры — чистая декорация, читалке он не нужен. */}
      <div className="receptionist__device">
        <span className="receptionist__island" aria-hidden="true" />

        <div className="receptionist__phone">
          {/* Аватар — как на экране вызова: круглый портрет собеседника. У
              салона портрет — его фасад (#1417); трубку берёт ассистент, о
              нём говорит контурный робот на уголке — тот же значок, что метит
              ответы бота в панели команды. Кадр — уменьшенная копия обложки
              «Showrooms», а не сам снимок 1600×900. */}
          <span className="receptionist__avatar" aria-hidden="true">
            <img
              className="receptionist__avatar-photo"
              src="/assets/showroom-avatar.webp"
              alt=""
              width="224"
              height="224"
              loading="lazy"
              decoding="async"
            />
            <span className="receptionist__avatar-bot">
              <Robot size={16} weight="regular" />
            </span>
          </span>
          {/* Имя — «ShiftGears» (#1417, решение автора витрины): демо-салон
              носит имя бренда, как в шапке и подвале сайта. Отдельная строка
              «ShiftGears» над аватаром снята — она повторяла бы имя. */}
          <span className="receptionist__name">
            {t('showcase.chats.callerName', 'ShiftGears')}
          </span>
          {/* Номер под именем — как в контакте на телефоне: имя говорит, КТО,
              номер подтверждает, что это настоящая линия салона, а не кнопка
              «написать». Берём тот же, что в подвале и на «Контактах». */}
          <span className="receptionist__number">{contactInfo.phone}</span>
          {/* Состояние линии — ТОЛЬКО во время разговора: там оно несёт
              счётчик, и «00:12» без подписи не читается. До нажатия строка
              говорила «Showroom line» — то же самое, что имя над ней, только
              капсом; на экране вызова до набора номера ничего не пишут.
              `aria-live` — чтобы смену состояния услышал и тот, кто не видит
              экран. */}
          <span className="receptionist__status" aria-live="polite">
            {calling
              ? `${t(
                  'showcase.chats.onCall',
                  'On the line'
                )} · ${formatDuration(seconds)}`
              : ''}
          </span>

          {/* Трубка прижата к низу экрана, как на телефоне: сверху — кто
            звонит, снизу — что с этим делать. */}
          <div className="receptionist__actions">
            {calling ? (
              <button
                type="button"
                className="receptionist__dial receptionist__dial--end"
                onClick={hangUp}
                aria-label={hangUpLabel}
                title={hangUpLabel}
              >
                <PhoneDisconnect size={28} weight="fill" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                className="receptionist__dial receptionist__dial--call"
                onClick={call}
                // Прежний звонок ещё закрывается (двойной клик по трубке):
                // новый набор подождёт, пока виджет выйдет из звонка.
                disabled={isolatedCallActive}
                aria-label={callLabel}
                title={callLabel}
              >
                <Phone size={28} weight="fill" aria-hidden="true" />
              </button>
            )}

            {/* Нотис — ДО нажатия, а не после: нажатие его и подтверждает
                (см. `rememberConsent`). Во время разговора он уходит: на
                экране вызова место занимают трубка и счётчик. */}
            {!calling && (
              <p className="receptionist__legal">
                {t('showcase.chats.callConsent', 'By calling you agree to')}{' '}
                <WidgetLink href={DEFAULT_TERMS_URL}>
                  {t('showcase.chats.terms', 'Terms of Service')}
                </WidgetLink>{' '}
                {t('showcase.chats.and', 'and')}{' '}
                <WidgetLink href={DEFAULT_PRIVACY_URL}>
                  {t('showcase.chats.privacy', 'Privacy Policy')}
                </WidgetLink>
              </p>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default ReceptionistCall;
